import { useMemo } from "react";
import { useAtomValue } from "jotai";
import { Icon } from "@common/index";
import { categoriesAtom } from "@/store/categoryAtoms";
import {
    PRICE_STOPS,
    PRICE_STOP_ALL_INDEX,
    priceStopIndexFor,
    type ProductFacetVendor,
} from "@models/productFacet";

interface FilterPanelProps {
    selectedCategoryIds: number[];
    onToggleCategory: (id: number) => void;
    /** Facet filters (#226). Null / empty / false all mean "not filtering on this". */
    maxPrice: number | null;
    selectedBrands: string[];
    selectedVendorIds: number[];
    inStockOnly: boolean;
    onMaxPriceChange: (value: number | null) => void;
    onToggleBrand: (value: string) => void;
    onToggleVendor: (id: number) => void;
    onInStockOnlyChange: (value: boolean) => void;
    /** Facet VALUES from GET /products/facets — the chips these two sections offer. */
    brands: string[];
    vendors: ProductFacetVendor[];
    /**
     * Whether to offer the In-Stock toggle at all. Brand and Vendor answer this for themselves
     * from their chip lists; In-Stock has no list, so the caller derives it from the facet
     * request's OUTCOME (`productFacetsStatusAtom === "loaded"`, plus an already-selected value
     * so an active filter always keeps a control that can clear it). Passed in rather than read
     * from the atom here because the mobile sheet must decide from the COMMITTED value — a
     * buffered toggle switched off mid-edit must not make its own control disappear.
     */
    showInStockOnly: boolean;
    /** Optional heading; sidebar shows it, sheets generally don't. */
    showHeading?: boolean;
    /**
     * Whether to offer the Category chips. False on a category page (#401 B4),
     * where the route already fixes the category and a chip group would
     * offer a second, conflicting answer to "which category".
     */
    showCategories?: boolean;
    /**
     * Whether to offer the Vendor chips. False on a vendor page (#425), where
     * the route fixes the vendor, for the reason showCategories gives.
     */
    showVendors?: boolean;
}

const chipClass = (selected: boolean) =>
    [
        "badge badge-lg cursor-pointer font-medium border gap-1",
        selected
            ? "badge-secondary border-secondary"
            : "bg-primary/10 border-primary/10 text-neutral hover:bg-primary/20",
    ].join(" ");

const sectionTitleClass = "text-xs font-bold text-neutral mb-2";

// The facet payload is taken as given everywhere else, but a brand column is free text and the
// server computes this list over the whole catalogue — so unset brands arrive as "" or "   ", and
// nothing upstream promises uniqueness. Both have to go before a chip is drawn (#226 review F7):
//
//   - A blank chip can NEVER latch. Clicking it writes ?brand= (or ?brand=%20), which parseFilters
//     drops by design, so the chip never shows selected, the grid never changes, and the shopper
//     can click it forever with no feedback. Normalising here is what keeps the chip list and
//     parseFilters agreeing about which values are real.
//   - Duplicates collide on the React key (the brand string is the key), which is a duplicate-key
//     warning and unstable reconciliation.
//
// Whitespace is trimmed only for the emptiness TEST, never off the value: parseFilters keeps
// " Acme" distinct from "Acme" deliberately, and collapsing them here would leave a live
// ?brand=%20Acme filter with no chip able to clear it.
//
// `?? ""` on a value typed string: the payload arrives through callApi's unchecked cast and the
// #399 guard checks the two containers only, so a null element is reachable here without ever
// being legal on the wire. It is treated exactly like a blank rather than allowed to throw in a
// render path, where it would take the whole customer app to GlobalError (review F1).
const usableBrands = (brands: string[]): string[] => [
    ...new Set(brands.filter((brand) => (brand ?? "").trim() !== "")),
];

// The label for a vendor with no usable name of its own. Two callers — a facet vendor whose name
// column is blank, and a selected id the facet read never returned — and they must agree, because
// the same vendor can be both.
const vendorFallbackName = (id: number): string => `Vendor ${id}`;

// The vendor counterpart of usableBrands (#394). Same two jobs, and both differences from the
// brand rule are deliberate:
//
//   - A blank name is RELABELLED, not dropped. The URL carries a vendor's id, not its name, so a
//     nameless chip still latches and still narrows the grid — dropping it would take away a
//     working control, which is the reverse of the blank-brand case, where the chip could never
//     latch at all. An unlabelled badge is the defect here, so the fix is a label. A null name
//     is a blank for the same reason it is above.
//   - Deduping is BY ID, because the id is the React key and the value the URL round-trips. Two
//     rows carrying one id are one filter however they are named. Vendor ids are unique
//     server-side today, so this half is a guard, not a fix for anything observed.
//
// Returned as the Map itself rather than its values: the same index answers "is this selected
// id already offered?" for the caller, which otherwise scanned the list once per selection.
// First occurrence wins, so the server's ordering survives.
const usableVendorsById = (
    vendors: ProductFacetVendor[],
): Map<number, ProductFacetVendor> => {
    const byId = new Map<number, ProductFacetVendor>();
    for (const vendor of vendors) {
        if (byId.has(vendor.id)) continue;
        byId.set(
            vendor.id,
            (vendor.name ?? "").trim() === ""
                ? { ...vendor, name: vendorFallbackName(vendor.id) }
                : vendor,
        );
    }
    return byId;
};

export const FilterPanel = ({
    selectedCategoryIds,
    onToggleCategory,
    maxPrice,
    selectedBrands,
    selectedVendorIds,
    inStockOnly,
    onMaxPriceChange,
    onToggleBrand,
    onToggleVendor,
    onInStockOnlyChange,
    brands,
    vendors,
    showInStockOnly,
    showHeading = true,
    showCategories = true,
    showVendors = true,
}: FilterPanelProps) => {
    // Top-level categories only for the chip list. Children are accessible
    // via the Browse hierarchy; surfacing every nested leaf here would
    // overwhelm the rail.
    const categories = useAtomValue(categoriesAtom);

    const priceIndex = priceStopIndexFor(maxPrice);
    const priceStop = PRICE_STOPS[priceIndex];

    // An active filter must always have a control that can turn it off. The facet read is
    // allowed to fail quietly, and a value can leave the catalogue after it was selected —
    // in either case the selection is still in the URL and still narrowing the grid, so it
    // is drawn alongside the facet values rather than hidden.
    // One Set over both sources: it drops a selection the facet list already offers (which is why
    // the old explicit filter is gone) and, in the same pass, a value repeated within either list.
    // Insertion order is preserved, so facet brands still come first and an off-list selection is
    // appended after them.
    const brandChips = [...new Set([...usableBrands(brands), ...selectedBrands])];
    // Memoised on the payload alone: the facet list changes when the read lands, while the
    // selections change on every chip toggle, and rebuilding the index for the latter is waste.
    const facetVendors = useMemo(() => usableVendorsById(vendors), [vendors]);
    // One Map over both sources, exactly as brandChips runs one Set: a selection the facet list
    // already offers is not appended again, and a selected id repeated in the URL
    // (?vendor=99&vendor=99 — parseFilters does not dedupe) draws one chip, not two under one
    // React key (review F2).
    const vendorChips = useMemo(() => {
        const byId = new Map(facetVendors);
        for (const id of selectedVendorIds) {
            // A selected id the facet read never returned has no name to show. The id is a
            // poor label but a working one: the point of the chip is that it can be cleared.
            if (!byId.has(id))
                byId.set(id, { id, name: vendorFallbackName(id) });
        }
        return [...byId.values()];
    }, [facetVendors, selectedVendorIds]);

    return (
        <div data-testid="filter-panel" className="px-4 py-3 flex flex-col gap-4">
            {showHeading && (
                <div className="text-sm font-bold text-neutral">Filters</div>
            )}
            {showCategories && (
                <section>
                    <div className={sectionTitleClass}>Category</div>
                    <div
                        role="group"
                        aria-label="Filter by category"
                        className="flex flex-wrap gap-2"
                    >
                        {categories.length === 0 && (
                            <span className="text-sm text-neutral/60">
                                Loading categories…
                            </span>
                        )}
                        {categories.map((cat) => {
                            const selected = selectedCategoryIds.includes(
                                cat.id,
                            );
                            return (
                                <button
                                    key={cat.id}
                                    type="button"
                                    role="checkbox"
                                    aria-checked={selected}
                                    onClick={() => onToggleCategory(cat.id)}
                                    className={chipClass(selected)}
                                >
                                    {cat.name}
                                </button>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* A native range input, not a custom-built slider: it is keyboard-operable and
                announced correctly for free. The thumb moves over STOP INDEXES rather than
                dollars, so the uneven money ladder ($25 → $50 → $75) gets even travel;
                aria-valuetext is what makes a screen reader say "$25" instead of "3". */}
            <section>
                <div className={sectionTitleClass}>Price Range</div>
                {/* range-primary is not decoration: the light theme sets
                    --color-base-content to #fff, which an unmodified .range draws
                    its track and thumb from — invisible on this base-100 surface. */}
                <input
                    type="range"
                    className="range range-sm range-primary w-full"
                    min={0}
                    max={PRICE_STOP_ALL_INDEX}
                    step={1}
                    value={priceIndex}
                    aria-label="Maximum price"
                    aria-valuetext={priceStop.label}
                    onChange={(event) =>
                        onMaxPriceChange(
                            PRICE_STOPS[Number(event.target.value)].maxPrice,
                        )
                    }
                />
                <div
                    aria-hidden="true"
                    className="flex justify-between mt-1 text-[10px] text-neutral/70"
                >
                    {PRICE_STOPS.map((stop) => (
                        <span key={stop.label}>{stop.label}</span>
                    ))}
                </div>
            </section>

            {/* Unlike Price — whose maxPrice the products query already honours — inStockOnly
                only reaches a server that has #226's API half. Rendering the toggle before then
                would give the shopper a control that latches ON and changes nothing, so it waits
                for positive proof the facets endpoint answered. See showInStockOnly. */}
            {showInStockOnly && (
                <section>
                    <label className="flex items-center gap-3 cursor-pointer">
                        {/* toggle-primary for the same reason as range-primary above —
                            an unmodified .toggle draws its border and knob from
                            --color-base-content (#fff on the light theme). */}
                        <input
                            type="checkbox"
                            className="toggle toggle-sm toggle-primary"
                            checked={inStockOnly}
                            onChange={(event) =>
                                onInStockOnlyChange(event.target.checked)
                            }
                        />
                        <span className="text-sm font-bold text-neutral">
                            In-Stock Only
                        </span>
                    </label>
                </section>
            )}

            {/* Brand and Vendor render only when there is something to show — a facet value
                or an active selection. An empty section header over no chips reads as a
                broken filter, and the facet fetch is allowed to fail quietly (see
                useProductFacetApi), so "no values" and "we couldn't load them" deliberately
                look the same: nothing. A SELECTED value is the exception; see brandChips. */}
            {brandChips.length > 0 && (
                <section>
                    <div className={sectionTitleClass}>Brand</div>
                    <div
                        role="group"
                        aria-label="Filter by brand"
                        className="flex flex-wrap gap-2"
                    >
                        {brandChips.map((brand) => {
                            const selected = selectedBrands.includes(brand);
                            return (
                                <button
                                    key={brand}
                                    type="button"
                                    role="checkbox"
                                    aria-checked={selected}
                                    onClick={() => onToggleBrand(brand)}
                                    className={chipClass(selected)}
                                >
                                    {brand}
                                    {/* The whole chip is the toggle, so this glyph must not
                                        be announced as a second control. No prop is needed:
                                        Icon hardcodes aria-hidden on its own span — see
                                        Components/Common/Icon.tsx. */}
                                    {selected && (
                                        <Icon
                                            name="close"
                                            className="text-sm"
                                        />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </section>
            )}

            {showVendors && vendorChips.length > 0 && (
                <section>
                    <div className={sectionTitleClass}>Vendor</div>
                    <div
                        role="group"
                        aria-label="Filter by vendor"
                        className="flex flex-wrap gap-2"
                    >
                        {vendorChips.map((vendor) => {
                            const selected = selectedVendorIds.includes(
                                vendor.id,
                            );
                            return (
                                <button
                                    key={vendor.id}
                                    type="button"
                                    role="checkbox"
                                    aria-checked={selected}
                                    onClick={() => onToggleVendor(vendor.id)}
                                    className={chipClass(selected)}
                                >
                                    {vendor.name}
                                    {selected && (
                                        <Icon
                                            name="close"
                                            className="text-sm"
                                        />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* TODO(post-MVP): the hardcoded Amount chips are hidden for MVP (#226). No
                backend field maps to them — the values were Figma placeholders, and toggling
                them wrote the URL without changing results. Post-MVP wires them to
                OptionValues; until then a filter that silently does nothing is worse than no
                filter. */}
        </div>
    );
};
